import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import {
  COUNTRY_OPTIONS,
  DROPDOWN_WIDTH,
  MENU_ITEM_HEIGHT,
} from '@sales-portal/domain/accounts/screens/employee-list/containers/employee-invite-drawer/components/searchable-country-dropdown/searchable-country-dropdown-constants';
import { CountryCode } from 'libphonenumber-js';
import { CountryOption } from '@sales-portal/domain/accounts/screens/employee-list/containers/employee-invite-drawer/components/searchable-country-dropdown/searchable-country-dropdown-types';
import { createStyles } from '@/contexts/theme-context';
import {
  Icons,
  SearchableDropdownMenu,
  Typography,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { View } from 'react-native';

interface SearchableCountryDropdownProps {
  onSelectCountry: (countryCode: CountryCode) => void;
  selectedCountryCode: CountryCode;
}

export const SearchableCountryDropdown: React.FC<
  SearchableCountryDropdownProps
> = ({ onSelectCountry, selectedCountryCode }) => {
  const [styles, theme] = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const selectedCountry = useMemo(
    () => COUNTRY_OPTIONS.find((c) => c.code === selectedCountryCode) ?? null,
    [selectedCountryCode],
  );

  const filteredCountries = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return COUNTRY_OPTIONS;
    }
    return COUNTRY_OPTIONS.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query) ||
        c.dialCode.includes(query),
    );
  }, [searchText]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchText('');
    }
  };

  const handleItemSelect = (item: CountryOption) => {
    handleOpenChange(false);
    if (item.code !== selectedCountryCode) {
      onSelectCountry(item.code);
    }
  };

  const triggerLabel = selectedCountry
    ? i18n.t('country_picker.selected_country_label', {
        country: selectedCountry.name,
        dialCode: selectedCountry.dialCode,
      })
    : i18n.t('select_a_country');

  return (
    <SearchableDropdownMenu
      dropdownWidth={DROPDOWN_WIDTH}
      emptyState={
        <View style={styles.emptyState}>
          <Typography color={theme.sk.color.text.secondary} size="$sm">
            {i18n.t('country_picker.no_results')}
          </Typography>
        </View>
      }
      estimatedItemHeight={MENU_ITEM_HEIGHT}
      items={filteredCountries}
      onItemSelect={handleItemSelect}
      onOpenChange={handleOpenChange}
      onSearchChange={setSearchText}
      open={isOpen}
      placement="bottom-start"
      renderItem={(item) => (
        <View
          accessibilityLabel={i18n.t('country_picker.option_label', {
            country: item.name,
            dialCode: item.dialCode,
          })}
          style={styles.option}
        >
          <View style={styles.optionLabel}>
            <Typography
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              size="$xl"
            >
              {item.flag}
            </Typography>
            <Typography
              flexShrink={1}
              numberOfLines={1}
              size="$sm"
              textOverflow="ellipsis"
            >
              {item.name}
            </Typography>
          </View>
          <Typography color={theme.sk.color.text.tertiary} size="$sm">
            {item.dialCode}
          </Typography>
        </View>
      )}
      searchPlaceholder={i18n.t('select_a_country')}
      searchText={searchText}
      trigger={
        <XStack
          accessibilityLabel={triggerLabel}
          accessibilityRole="button"
          accessibilityState={{ expanded: isOpen }}
          alignItems="center"
          gap="$4"
          justifyContent="center"
          paddingHorizontal="$12"
        >
          {selectedCountry ? (
            <Typography
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              size="$xl"
            >
              {selectedCountry.flag}
            </Typography>
          ) : null}
          <Typography size="$sm">
            {selectedCountry?.dialCode ?? i18n.t('select_a_country')}
          </Typography>
          <Icons.ChevronDown
            color={theme.sk.color.icon.secondary}
            flexShrink={0}
            size="$14"
          />
        </XStack>
      }
    />
  );
};

const useStyles = createStyles((theme) => ({
  emptyState: {
    alignItems: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  option: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  optionLabel: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    maxWidth: 150,
  },
}));
